/* eslint-disable max-lines-per-function, max-statements */
import { useLazyQuery, useMutation } from "@apollo/client";
import { mutations, queries } from "@bluframe/mightychroma-mightytool-graphql-schema";
import useEightLeggedFreakNewsCriticsList from "hooks/useEightLeggedFreakNewsCriticsList";
import useNewsCritics from "hooks/useNewsCritics";
import usePreviewsNewsCritics from "hooks/usePreviewsNewsCritics";
import useUser from "hooks/useUser";
import { useCallback, useEffect, useMemo } from "react";
const { news: { ADD_PREVIEW_NEWS_SOURCE }, eightLeggedFreak: { EIGHT_LEGGED_FREAK_NEWS_SOURCE_UPDATE } } = mutations;
const { GET_OMDB_ID } = queries;
const ZERO = 0;
const THREE = 3;
const useNewsCard = ({ name, serviceName, setIMDbId }) => {
    const { addImage } = usePreviewsNewsCritics();
    const { setIsToggleFlaggedLoading, setSelectedId, setSelectedIsPreviewLoading, setSelectedIsPreviewValid, state } = useNewsCritics();
    const { isAdmin, isEditor } = useUser();
    const serviceState = useMemo(() => state[serviceName], [state, serviceName]);
    const [addPreview, { data: addPreviewData, loading: addPreviewLoading }] = useMutation(ADD_PREVIEW_NEWS_SOURCE, {
        onCompleted: (data) => {
            if (data.addPreviewNewsSource.image) {
                // Add image to image selector
                addImage({
                    originalUrl: data.addPreviewNewsSource.image,
                    role: "front"
                });
            }
        }
    });
    const [getOmdbId] = useLazyQuery(GET_OMDB_ID, {
        onCompleted: (omdbIdData) => {
            if (setIMDbId && omdbIdData?.omdbId) {
                setIMDbId(omdbIdData.omdbId);
            }
        }
    });
    const { refetch } = useEightLeggedFreakNewsCriticsList(serviceName);
    const [eightLeggedFreakNewsCriticsUpdate, { loading: flagLoading }] = useMutation(EIGHT_LEGGED_FREAK_NEWS_SOURCE_UPDATE);
    useEffect(() => {
        setIsToggleFlaggedLoading(flagLoading);
        // It just goes into infinite loop if we include
        // setIsToggleFlaggedLoading in the dependency array
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flagLoading]);
    useEffect(() => {
        if (serviceState.selected.url) {
            addPreview({
                variables: {
                    previewNewsSourceAddInput: {
                        overrides: serviceState.selected.overrides,
                        serviceName,
                        url: serviceState.selected.url
                    }
                }
            });
        }
    }, [
        addPreview,
        serviceName,
        serviceState.selected.overrides,
        serviceState.selected.url
    ]);
    useEffect(() => {
        if (addPreviewData) {
            setSelectedIsPreviewValid(addPreviewData.addPreviewNewsSource.isValid, serviceName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addPreviewData]);
    useEffect(() => {
        setSelectedIsPreviewLoading(addPreviewLoading, serviceName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addPreviewLoading]);
    const display = useMemo(() => {
        if (serviceState.isOnlyNew) {
            return serviceState.display.filter(({ isNew }) => isNew);
        }
        if (serviceState.isOnlyPresent) {
            return serviceState.display.filter(({ isNew }) => !isNew);
        }
        return serviceState.display;
    }, [serviceState.display, serviceState.isOnlyNew, serviceState.isOnlyPresent]);
    const filteredDisplay = useMemo(() => {
        if (serviceState.isShowFlagged) {
            return display;
        }
        return display.filter(({ isFlagged }) => !isFlagged);
    }, [display, serviceState.isShowFlagged]);
    const subheader = useMemo(() => {
        if (serviceState.isOnlyNew) {
            return `Total New: ${filteredDisplay.length}`;
        }
        if (serviceState.isOnlyPresent) {
            return `Total Present: ${filteredDisplay.length}`;
        }
        return `Total Available: ${filteredDisplay.length}`;
    }, [
        filteredDisplay.length,
        serviceState.isOnlyNew,
        serviceState.isOnlyPresent
    ]);
    const title = useMemo(() => `${name} News`, [name]);
    const avatar = useMemo(() => name.slice(ZERO, THREE), [name]);
    const onToggleFlagged = useCallback((id, isFlagged) => () => {
        eightLeggedFreakNewsCriticsUpdate({
            onCompleted: () => {
                refetch({
                    eightLeggedFreakNewsSourcesListInput: {
                        serviceName
                    }
                });
            },
            variables: {
                eightLeggedFreakNewsSourceUpdateInput: {
                    id,
                    isFlagged: !isFlagged
                },
                serviceName
            }
        });
    }, [eightLeggedFreakNewsCriticsUpdate, refetch, serviceName]);
    const onSelect = useCallback((id) => {
        setSelectedId(id, serviceName);
        if (setIMDbId) {
            getOmdbId({
                variables: {
                    id: id,
                    serviceName
                }
            });
        }
    }, [getOmdbId, serviceName, setIMDbId, setSelectedId]);
    return {
        avatar,
        display: filteredDisplay,
        flagLoading,
        isShowFlagButton: isAdmin || isEditor,
        onSelect,
        onToggleFlagged,
        selected: serviceState.selected.id,
        serviceName,
        subheader,
        title
    };
};
export default useNewsCard;
